import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BaseComponent } from '../../types';

interface HeaderProps extends BaseComponent {
  title?: string;
  activePage?: string;
}

export function Header({
  className,
  children,
  title = 'CommerceMinder',
  activePage = 'Anasayfa',
}: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showCTAButton, setShowCTAButton] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.querySelector('[data-hero-section]');
      if (heroSection) {
        const heroRect = heroSection.getBoundingClientRect();
        setShowCTAButton(heroRect.bottom <= 0);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const navLinks = [
    { name: 'Anasayfa', href: '/' },
    { name: 'İletişim', href: '/contact' },
    { name: 'Siber Güvenlik', href: '/cyber-security' },
    { name: 'Hakkımızda', href: '/about' },
  ];

  return (
    <>
      <header
        className={`w-full h-16 backdrop-blur-[15px] flex-shrink-0 fixed top-0 z-50 ${className || ''}`}
        style={{
          alignSelf: 'stretch',
          borderBottom: '0.5px solid rgba(255, 255, 255, 0.12)',
          backgroundColor: '#020816',
        }}
      >
        <div className="max-w-[1440px] mx-auto w-full h-full flex items-center px-4 md:px-10">
          {/* Logo ve Marka */}
          <Link
            to="/"
            className={`flex items-center hover:opacity-80 transition-all duration-700 ease-out ${
              isLoaded
                ? 'opacity-100 transform translate-x-0'
                : 'opacity-0 transform -translate-x-8'
            }`}
          >
            <img
              src="/logo.png"
              alt="CommerceMinder Logo"
              className="w-[23px] h-[26px] flex-shrink-0 mr-2 md:mr-2"
              style={{ aspectRatio: '23/26' }}
            />
            <h1
              style={{
                fontFamily: 'Red Hat Display',
                fontSize: '22px',
                fontStyle: 'normal',
                fontWeight: 400,
                lineHeight: '28px',
                background:
                  'linear-gradient(274deg, rgba(255, 255, 255, 0.30) 0%, #FFF 25%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {title}
            </h1>
          </Link>

          {/* Desktop Navigation Menu */}
          <nav
            className={`hidden lg:block ml-auto mr-4 transition-all duration-700 ease-out ${
              isLoaded
                ? 'opacity-100 transform translate-x-0'
                : 'opacity-0 transform translate-x-8'
            }`}
          >
            <ul className="flex items-center space-x-8">
              {navLinks.map((link, index) => {
                const isActive = activePage === link.name;
                return (
                  <li
                    key={index}
                    className={`transition-all duration-700 ease-out ${
                      isLoaded
                        ? 'opacity-100 transform translate-y-0'
                        : 'opacity-0 transform -translate-y-2'
                    }`}
                    style={{ transitionDelay: `${index * 100}ms` }}
                  >
                    {link.href.startsWith('#') ? (
                      <a
                        href={link.href}
                        className={`font-inter text-base font-medium leading-[26px] tracking-[0.16px] transition-colors duration-200 hover:text-text-white flex items-center gap-1 ${
                          isActive ? 'text-text-white' : 'text-[#AEAEB8]'
                        }`}
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className={`font-inter text-base font-medium leading-[26px] tracking-[0.16px] transition-colors duration-200 hover:text-text-white flex items-center gap-1 ${
                          isActive ? 'text-text-white' : 'text-[#AEAEB8]'
                        }`}
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* CTA Button */}
          {showCTAButton && (
            <div className="hidden lg:block ml-6">
              <button
                onClick={() => navigate('/contact')}
                className="transition-all duration-300"
                style={{
                  display: 'flex',
                  height: '40px',
                  padding: '8px 20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '2px',
                  borderRadius: '8px',
                  border: 'none',
                  background:
                    'linear-gradient(180deg, #025BCF 0%, #0091BD 100%)',
                  color: '#FFF',
                  fontFamily: 'Manrope',
                  fontSize: '16px',
                  fontStyle: 'normal',
                  fontWeight: '500',
                  lineHeight: '26px',
                  cursor: 'pointer',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background =
                    'linear-gradient(180deg, #0091BD 0%, #025BCF 100%)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background =
                    'linear-gradient(180deg, #025BCF 0%, #0091BD 100%)';
                }}
              >
                Ücretsiz Deneyin
              </button>
            </div>
          )}

          {/* Desktop Children */}
          {children && (
            <div className="hidden lg:flex ml-6 items-center space-x-4">
              {children}
            </div>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden ml-auto p-2 text-text-white hover:text-brand-mint-green focus:outline-none focus:ring-2 focus:ring-brand-mint-green rounded"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              {isMobileMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed top-16 left-0 right-0 bg-base-dark-navy/95 border-t border-brand-ocean-blue/20 backdrop-blur-[15px] z-40">
          <nav className="px-4 py-4">
            <ul className="space-y-3">
              {navLinks.map((link, index) => {
                const isActive = activePage === link.name;
                return (
                  <li key={index}>
                    {link.href.startsWith('#') ? (
                      <a
                        href={link.href}
                        className={`py-2 font-inter text-base font-medium leading-[26px] tracking-[0.16px] transition-colors duration-200 hover:text-text-white flex items-center gap-1 ${
                          isActive ? 'text-text-white' : 'text-text-body'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {link.name}
                      </a>
                    ) : (
                      <Link
                        to={link.href}
                        className={`py-2 font-inter text-base font-medium leading-[26px] tracking-[0.16px] transition-colors duration-200 hover:text-text-white flex items-center gap-1 ${
                          isActive ? 'text-text-white' : 'text-text-body'
                        }`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {link.name}
                      </Link>
                    )}
                  </li>
                );
              })}
            </ul>
            {children && (
              <div className="mt-4 pt-4 border-t border-brand-ocean-blue/20">
                {children}
              </div>
            )}
          </nav>
        </div>
      )}
    </>
  );
}
